import { PiiCategory, RedactStrategy, getFieldMeta, resolveStrategy } from "../models/meta.mjs";
import { anonymizeEmail, anonymizeEmailsInString } from "./anonymizeUtils.mjs";

// src/utils/piiUtils.ts
/** Sentinel returned by {@link redactLeaf} to indicate the key should be removed. */
const DROP_SENTINEL = Symbol("pii.drop");
const EMAILISH = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
function isEmailLike(value) {
	return EMAILISH.test(value.trim());
}
function maskString(value) {
	const t = value.trim();
	if (t.length <= 1) return "*";
	if (t.length === 2) return `${t[0]}*`;
	return `${t[0]}${"*".repeat(t.length - 2)}${t[t.length - 1]}`;
}
/**
* 64-bit FNV-1a hash, UTF-8 encoded input, returned as `h` + 16 hex chars.
*
* Used as a stable deterministic fingerprint for warehouse join keys. This is
* NOT a cryptographic hash — for low-entropy inputs (emails, SSNs, phone
* numbers) it is trivially reversible by brute force. The `drop` strategy
* (see {@link PiiCategory}) is the answer for fields that must not be
* recoverable; `hash` is only for preserving joinability across tables.
*
* Collision probability (birthday bound ≈ N² / 2^65): at 10M records
* ~2.7e-6; at 100M records ~2.7e-4. Sufficient for customer-scale warehouse
* joins and a large improvement over the previous 32-bit FNV-1a, which had
* a 50% collision probability at only ~77k values.
*
* Kept synchronous and dependency-free because this file is imported by
* the browser bundle via `scrubForExternalUse` / `redactUnknownPii`, which
* rules out `node:crypto` and async `crypto.subtle.digest`.
*/
const FNV64_OFFSET_BASIS = BigInt("0xcbf29ce484222325");
const FNV64_PRIME = BigInt("0x100000001b3");
const FNV64_MASK = BigInt("0xffffffffffffffff");
const FNV64_UTF8_ENCODER = new TextEncoder();
function shortDeterministicHash(input) {
	const bytes = FNV64_UTF8_ENCODER.encode(input);
	let h = FNV64_OFFSET_BASIS;
	for (let i = 0; i < bytes.length; i++) {
		h ^= BigInt(bytes[i]);
		h = h * FNV64_PRIME & FNV64_MASK;
	}
	return `h${h.toString(16).padStart(16, "0")}`;
}
/**
* Opaque deterministic id for Sentry `user.id` without sending raw Firebase UID to the vendor.
* Uses the same FNV-1a 64-bit helper as PII `hash` redaction — not cryptographic; use only as a correlator.
*/
function hashForSentryUserId(uid) {
	return shortDeterministicHash(uid);
}
function stringifyForHash(value) {
	if (value === null || value === void 0) return "";
	if (typeof value === "string") return value;
	try {
		return JSON.stringify(value);
	} catch {
		return "";
	}
}
/** HIPAA Safe Harbor #3: retain year only. Accepts ISO strings, `Date`, or epoch ms. */
function truncateYear(value) {
	if (value === null || value === void 0) return value;
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? "[REDACTED]" : value.getUTCFullYear();
	if (typeof value === "number") {
		const d = new Date(value);
		return Number.isNaN(d.getTime()) ? "[REDACTED]" : d.getUTCFullYear();
	}
	if (typeof value === "string") {
		const m = value.match(/^(\d{4})/);
		if (m) return m[1];
		const d = new Date(value);
		return Number.isNaN(d.getTime()) ? "[REDACTED]" : String(d.getUTCFullYear());
	}
	return "[REDACTED]";
}
/** HIPAA Safe Harbor #2: keep first 3 ZIP digits, zero the rest. */
function truncateZip3(value) {
	if (typeof value !== "string") return "[REDACTED]";
	const digits = value.replace(/\D/g, "");
	if (digits.length < 3) return "[REDACTED]";
	return `${digits.slice(0, 3)}**`;
}
function anonymizeIp(value) {
	if (typeof value !== "string") return "[REDACTED]";
	const v4 = value.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.\d{1,3}$/);
	if (v4) return `${v4[1]}.${v4[2]}.${v4[3]}.0`;
	if (value.includes(":")) {
		const [prefix, suffix] = value.split("::");
		const pParts = prefix ? prefix.split(":").filter((p) => p.length > 0) : [];
		const sParts = suffix ? suffix.split(":").filter((p) => p.length > 0) : [];
		const gap = Math.max(0, 8 - (pParts.length + sParts.length));
		return `${[
			...pParts,
			...new Array(gap).fill("0"),
			...sParts
		].slice(0, 3).join(":")}::`;
	}
	return "[REDACTED]";
}
function applyMask(value, category) {
	if (typeof value === "string") {
		if (category === "email" || isEmailLike(value)) return anonymizeEmail(value);
		return maskString(value);
	}
	return "[REDACTED]";
}
/** Returns {@link DROP_SENTINEL} to signal the caller should delete the key. */
function redactLeaf(value, meta) {
	if (value === null || value === void 0) return value;
	const strategy = resolveStrategy(meta);
	if (Array.isArray(value) && strategy !== "drop" && strategy !== "redact" && strategy !== "hash") return value.map((v) => redactLeaf(v, meta)).filter((v) => v !== DROP_SENTINEL);
	switch (strategy) {
		case "redact": return "[REDACTED]";
		case "hash": return shortDeterministicHash(stringifyForHash(value));
		case "mask": return applyMask(value, meta.category);
		case "truncateYear": return truncateYear(value);
		case "truncateZip3": return truncateZip3(value);
		case "anonymizeIp": return anonymizeIp(value);
		case "drop": return DROP_SENTINEL;
	}
}
function readZodDef(schema) {
	return schema._zod?.def;
}
function readLazyInner(schema) {
	const def = readZodDef(schema);
	if (def?.type !== "lazy" || !def.getter) return void 0;
	try {
		return def.getter();
	} catch {
		return;
	}
}
/**
* Peels Zod v4 wrappers via `schema._zod.def` (not Zod v3 `_def`):
* `optional` / `nullable` / `default` / `catch` / `prefault` / `readonly` / `nonoptional`,
* and `pipe` (from `.transform()`, `.pipe()`, and similar — input schema is `def.in`).
* Missing `_zod` (wrong Zod major / non-schema) returns the input unchanged instead of throwing.
*/
function unwrapWrappers(schema) {
	const def = readZodDef(schema);
	if (!def) return schema;
	const t = def.type;
	if (t === "optional" || t === "nullable" || t === "default" || t === "catch" || t === "prefault" || t === "readonly" || t === "nonoptional") {
		const inner = def.innerType;
		if (inner) return unwrapWrappers(inner);
	}
	if (t === "pipe") {
		const input = def.in;
		if (input) return unwrapWrappers(input);
	}
	return schema;
}
function collectPiiForField(fieldSchema, path, seen = /* @__PURE__ */ new WeakSet()) {
	const out = [];
	const inner = unwrapWrappers(fieldSchema);
	const piiMeta = getFieldMeta(fieldSchema)?.pii;
	if (piiMeta) out.push({
		path,
		meta: piiMeta
	});
	const innerDef = readZodDef(inner);
	const t = innerDef?.type;
	if (t === "object") {
		out.push(...getPiiFields(inner, path, seen));
		return out;
	}
	if (typeof inner === "object" && inner !== null) {
		if (seen.has(inner)) return out;
		seen.add(inner);
	}
	if (t === "array") {
		const el = innerDef?.element;
		if (el) out.push(...collectPiiForField(el, `${path}.*`, seen));
	} else if (t === "union") for (const option of innerDef?.options ?? []) out.push(...collectPiiForField(option, path, seen));
	else if (t === "intersection") {
		if (innerDef?.left) out.push(...collectPiiForField(innerDef.left, path, seen));
		if (innerDef?.right) out.push(...collectPiiForField(innerDef.right, path, seen));
	} else if (t === "record") {
		const valueType = innerDef?.valueType;
		if (valueType) out.push(...collectPiiForField(valueType, `${path}.*`, seen));
	} else if (t === "map") {
		if (innerDef?.keyType) out.push(...collectPiiForField(innerDef.keyType, `${path}.*`, seen));
		if (innerDef?.valueType) out.push(...collectPiiForField(innerDef.valueType, `${path}.*`, seen));
	} else if (t === "set") {
		const valueType = innerDef?.valueType;
		if (valueType) out.push(...collectPiiForField(valueType, `${path}.*`, seen));
	} else if (t === "lazy") {
		const target = readLazyInner(inner);
		if (target) out.push(...collectPiiForField(target, path, seen));
	}
	if (typeof inner === "object" && inner !== null) seen.delete(inner);
	return out;
}
/**
* Returns `{ path, meta }` for every PII-registered field under the schema.
* Paths use `.` for nesting; a `*` segment means each array index, e.g. `registrants.*.email`
* for `z.object({ registrants: z.array(z.object({ email: meta(z.string().email(), { pii: { category: PiiCategory.Email } }) })) })`.
* Top-level `z.array(...)` yields paths starting with `*` (e.g. `*.email`).
*/
function getPiiFields(schema, prefix = "", seen = /* @__PURE__ */ new WeakSet()) {
	const out = [];
	const inner = unwrapWrappers(schema);
	if (typeof inner === "object" && inner !== null) {
		if (seen.has(inner)) return out;
		seen.add(inner);
	}
	const innerDef = readZodDef(inner);
	const t = innerDef?.type;
	if (t === "object") {
		const shape = inner.shape;
		for (const key of Object.keys(shape)) {
			const path = prefix ? `${prefix}.${key}` : key;
			out.push(...collectPiiForField(shape[key], path, seen));
		}
	} else if (t === "array") {
		const el = innerDef?.element;
		if (el) out.push(...collectPiiForField(el, prefix ? `${prefix}.*` : "*", seen));
	} else if (t === "union") for (const option of innerDef?.options ?? []) out.push(...getPiiFields(option, prefix, seen));
	else if (t === "intersection") {
		if (innerDef?.left) out.push(...getPiiFields(innerDef.left, prefix, seen));
		if (innerDef?.right) out.push(...getPiiFields(innerDef.right, prefix, seen));
	} else if (t === "record") {
		const valueType = innerDef?.valueType;
		if (valueType) out.push(...collectPiiForField(valueType, prefix ? `${prefix}.*` : "*", seen));
	} else if (t === "map") {
		if (innerDef?.keyType) out.push(...collectPiiForField(innerDef.keyType, prefix ? `${prefix}.*` : "*", seen));
		if (innerDef?.valueType) out.push(...collectPiiForField(innerDef.valueType, prefix ? `${prefix}.*` : "*", seen));
	} else if (t === "set") {
		const valueType = innerDef?.valueType;
		if (valueType) out.push(...collectPiiForField(valueType, prefix ? `${prefix}.*` : "*", seen));
	} else if (t === "lazy") {
		const target = readLazyInner(inner);
		if (target) out.push(...getPiiFields(target, prefix, seen));
	}
	if (typeof inner === "object" && inner !== null) seen.delete(inner);
	return out;
}
/**
* Deep clone for schema-driven redaction: prefers `structuredClone`, then falls back to
* a walk over plain objects, arrays, `Map`, and `Set` so logging never throws on functions,
* symbols, or class instances. Other non-plain objects are copied via enumerable keys so
* {@link redactPiiFromObject} never mutates the original input.
*/
function clonePlainForRedaction(value, seen) {
	if (value === null || typeof value !== "object") return value;
	if (seen.has(value)) return seen.get(value);
	if (value instanceof Date) return new Date(value.getTime());
	if (value instanceof Error) {
		const copy = {
			name: value.name,
			message: value.message,
			stack: value.stack
		};
		seen.set(value, copy);
		const rec = value;
		for (const key of Object.keys(rec)) copy[key] = clonePlainForRedaction(rec[key], seen);
		return copy;
	}
	if (Array.isArray(value)) {
		const copy = [];
		seen.set(value, copy);
		for (let i = 0; i < value.length; i++) copy.push(clonePlainForRedaction(value[i], seen));
		return copy;
	}
	if (value instanceof Map) {
		const copy = /* @__PURE__ */ new Map();
		seen.set(value, copy);
		for (const [k, v] of value) copy.set(clonePlainForRedaction(k, seen), clonePlainForRedaction(v, seen));
		return copy;
	}
	if (value instanceof Set) {
		const copy = /* @__PURE__ */ new Set();
		seen.set(value, copy);
		for (const v of value) copy.add(clonePlainForRedaction(v, seen));
		return copy;
	}
	const out = {};
	seen.set(value, out);
	const rec = value;
	for (const key of Object.keys(rec)) out[key] = clonePlainForRedaction(rec[key], seen);
	return out;
}
function cloneDeep(value) {
	try {
		return structuredClone(value);
	} catch {
		return clonePlainForRedaction(value, /* @__PURE__ */ new WeakMap());
	}
}
function getByPath(root, path) {
	const parts = path.split(".");
	let cur = root;
	for (const p of parts) {
		if (cur === null || cur === void 0) return void 0;
		if (typeof cur !== "object") return void 0;
		cur = cur[p];
	}
	return cur;
}
/** A path segment `*` (from e.g. `z.array` traversal) matches each array index. */
function pathHasArrayWildcard(path) {
	return path.split(".").some((s) => s === "*");
}
/** Visits every concrete path in `data` that matches `pattern` (e.g. `a.*.b.c`). */
function forEachPathMatch(data, pattern, visit) {
	const parts = pattern.split(".");
	const walk = (cur, i, acc) => {
		if (i === parts.length) {
			visit(acc.join("."), cur);
			return;
		}
		const seg = parts[i];
		if (seg === "*") {
			if (Array.isArray(cur)) {
				for (let j = 0; j < cur.length; j++) {
					const next = cur[j];
					walk(next, i + 1, [...acc, String(j)]);
				}
				return;
			}
			if (cur === null || cur === void 0 || typeof cur !== "object") return;
			for (const key of Object.keys(cur)) {
				const next = cur[key];
				walk(next, i + 1, [...acc, key]);
			}
		} else {
			if (cur === null || cur === void 0 || typeof cur !== "object") return;
			if (!Object.prototype.hasOwnProperty.call(cur, seg)) return;
			const next = cur[seg];
			walk(next, i + 1, [...acc, seg]);
		}
	};
	walk(data, 0, []);
}
/** True if `path` exists on `root` (own keys at each segment). Skips optional fields absent from data. */
function pathExists(root, path) {
	const parts = path.split(".");
	let cur = root;
	for (const p of parts) {
		if (cur === null || cur === void 0) return false;
		if (typeof cur !== "object") return false;
		if (!Object.prototype.hasOwnProperty.call(cur, p)) return false;
		cur = cur[p];
	}
	return true;
}
function setByPath(root, path, next) {
	const parts = path.split(".");
	if (parts.length === 0) return;
	let cur = root;
	for (let i = 0; i < parts.length - 1; i++) {
		const p = parts[i];
		if (cur === null || cur === void 0) return;
		if (typeof cur !== "object") return;
		const obj = cur;
		if (obj[p] === null || obj[p] === void 0 || typeof obj[p] !== "object") obj[p] = {};
		cur = obj[p];
	}
	const last = parts[parts.length - 1];
	if (cur !== null && cur !== void 0 && typeof cur === "object") cur[last] = next;
}
function deleteByPath(root, path) {
	const parts = path.split(".");
	if (parts.length === 0) return;
	let cur = root;
	for (let i = 0; i < parts.length - 1; i++) {
		const p = parts[i];
		if (cur === null || cur === void 0) return;
		if (typeof cur !== "object") return;
		cur = cur[p];
	}
	const last = parts[parts.length - 1];
	if (cur !== null && cur !== void 0 && typeof cur === "object") {
		if (Array.isArray(cur) && /^\d+$/u.test(last)) {
			const idx = Number(last);
			if (idx >= 0 && idx < cur.length) cur.splice(idx, 1);
			return;
		}
		delete cur[last];
	}
}
function redactPiiFromObject(data, paths) {
	const clone = cloneDeep(data);
	for (const { path, meta } of paths) if (pathHasArrayWildcard(path)) {
		const matches = [];
		forEachPathMatch(clone, path, (concrete, current) => {
			matches.push({
				concrete,
				current
			});
		});
		for (let i = matches.length - 1; i >= 0; i--) {
			const { concrete, current } = matches[i];
			const next = redactLeaf(current, meta);
			if (next === DROP_SENTINEL) deleteByPath(clone, concrete);
			else setByPath(clone, concrete, next);
		}
	} else {
		if (!pathExists(clone, path)) continue;
		const next = redactLeaf(getByPath(clone, path), meta);
		if (next === DROP_SENTINEL) deleteByPath(clone, path);
		else setByPath(clone, path, next);
	}
	return clone;
}
function redactPii(data, schema) {
	const inner = unwrapWrappers(schema);
	const meta = getFieldMeta(schema)?.pii;
	if (meta) {
		const next = redactLeaf(data, meta);
		return next === DROP_SENTINEL ? void 0 : next;
	}
	const paths = getPiiFields(inner);
	if (paths.length === 0) return data;
	return redactPiiFromObject(data, paths);
}
/** Lowercase names; match with {@link isUnknownPiiKey}. */
const UNKNOWN_PII_KEYS = /* @__PURE__ */ new Set([
	"email",
	"firstname",
	"lastname",
	"username",
	"phone",
	"mobilephone",
	"address",
	"street",
	"city",
	"state",
	"zip",
	"zipcode",
	"postalcode",
	"birthdate",
	"dob",
	"ssn",
	"socialsecuritynumber",
	"authorization",
	"cookie",
	"set-cookie",
	"password",
	"token",
	"secret",
	"apikey",
	"accesstoken",
	"refreshtoken",
	"id_token",
	"cardnumber",
	"cvv"
]);
function isUnknownPiiKey(key) {
	return UNKNOWN_PII_KEYS.has(key.toLowerCase());
}
function redactUnknownPiiInner(data, cache, active) {
	if (data === null || data === void 0) return data;
	if (typeof data === "string") return anonymizeEmailsInString(data);
	if (typeof data !== "object") return data;
	if (data instanceof Date) return data;
	if (typeof data.toDate === "function") return data;
	if (cache.has(data)) return cache.get(data);
	if (active.has(data)) return "[CIRCULAR]";
	active.add(data);
	if (Array.isArray(data)) {
		const out = [];
		cache.set(data, out);
		for (const item of data) out.push(redactUnknownPiiInner(item, cache, active));
		active.delete(data);
		return out;
	}
	if (data instanceof Map) {
		const out = /* @__PURE__ */ new Map();
		cache.set(data, out);
		for (const [key, value] of data) {
			const nextKey = typeof key === "string" ? anonymizeEmailsInString(key) : redactUnknownPiiInner(key, cache, active);
			if (typeof key === "string" && isUnknownPiiKey(key)) out.set(nextKey, value === null || value === void 0 ? value : "[REDACTED]");
			else out.set(nextKey, redactUnknownPiiInner(value, cache, active));
		}
		active.delete(data);
		return out;
	}
	if (data instanceof Set) {
		const out = /* @__PURE__ */ new Set();
		cache.set(data, out);
		for (const value of data) out.add(redactUnknownPiiInner(value, cache, active));
		active.delete(data);
		return out;
	}
	const obj = data;
	const out = {};
	cache.set(data, out);
	for (const key of Object.keys(obj)) {
		const v = obj[key];
		const safeKey = anonymizeEmailsInString(key);
		if (isUnknownPiiKey(key)) out[safeKey] = v === null || v === void 0 ? v : "[REDACTED]";
		else out[safeKey] = redactUnknownPiiInner(v, cache, active);
	}
	active.delete(data);
	return out;
}
function redactUnknownPii(data) {
	return redactUnknownPiiInner(data, /* @__PURE__ */ new WeakMap(), /* @__PURE__ */ new WeakSet());
}
const SENSITIVE_HEADER_NAMES = /* @__PURE__ */ new Set([
	"authorization",
	"cookie",
	"set-cookie",
	"x-api-key",
	"x-forwarded-authorization",
	"proxy-authorization"
]);
/** Redacts sensitive HTTP headers for Sentry / logs. */
function sanitizeHttpHeaders(headers) {
	if (headers === null || headers === void 0) return void 0;
	if (typeof headers !== "object") return void 0;
	const out = {};
	for (const [rawKey, rawVal] of Object.entries(headers)) {
		const lower = rawKey.toLowerCase();
		if (SENSITIVE_HEADER_NAMES.has(lower)) out[rawKey] = "[REDACTED]";
		else if (Array.isArray(rawVal)) out[rawKey] = rawVal.map(String).join(", ");
		else out[rawKey] = String(rawVal ?? "");
	}
	return out;
}
function scrubForExternalUseInner(value, cache, active) {
	if (value === null || value === void 0) return value;
	if (typeof value === "string") return anonymizeEmailsInString(value);
	if (typeof value !== "object") return value;
	if (value instanceof Date) return value;
	if (typeof value.toDate === "function") return value;
	if (cache.has(value)) return cache.get(value);
	if (active.has(value)) return "[CIRCULAR]";
	active.add(value);
	if (Array.isArray(value)) {
		const out = [];
		cache.set(value, out);
		for (const item of value) out.push(scrubForExternalUseInner(item, cache, active));
		active.delete(value);
		return out;
	}
	if (value instanceof Map) {
		const out = /* @__PURE__ */ new Map();
		cache.set(value, out);
		for (const [k, v] of value) {
			const safeKey = typeof k === "string" ? anonymizeEmailsInString(k) : scrubForExternalUseInner(k, cache, active);
			if (typeof k === "string" && isUnknownPiiKey(k)) out.set(safeKey, v === null || v === void 0 ? v : "[REDACTED]");
			else out.set(safeKey, scrubForExternalUseInner(v, cache, active));
		}
		active.delete(value);
		return out;
	}
	if (value instanceof Set) {
		const out = /* @__PURE__ */ new Set();
		cache.set(value, out);
		for (const v of value) out.add(scrubForExternalUseInner(v, cache, active));
		active.delete(value);
		return out;
	}
	const obj = value;
	const out = {};
	cache.set(value, out);
	for (const key of Object.keys(obj)) {
		const v = obj[key];
		const safeKey = anonymizeEmailsInString(key);
		if (isUnknownPiiKey(key)) out[safeKey] = v === null || v === void 0 ? v : "[REDACTED]";
		else out[safeKey] = scrubForExternalUseInner(v, cache, active);
	}
	active.delete(value);
	return out;
}
/**
* Recursively removes or masks PII suitable for any outbound surface (structured logs,
* error reporters, support exports, third-party tools). Not tied to a specific product.
*/
function scrubForExternalUse(value) {
	return scrubForExternalUseInner(value, /* @__PURE__ */ new WeakMap(), /* @__PURE__ */ new WeakSet());
}
/** Structured log / APM payloads when scrubbing must not throw. */
const LOG_SCRUB_OMITTED = {
	logPayloadOmitted: true,
	reason: "scrub_failed"
};
/** Never throws; prefer at logging and Sentry boundaries. */
function redactUnknownPiiSafe(data) {
	try {
		return redactUnknownPii(data);
	} catch {
		return { ...LOG_SCRUB_OMITTED };
	}
}
/** Never throws; prefer when {@link redactPii} is used from `safeLog` or similar. */
function redactPiiSafe(data, schema) {
	try {
		return redactPii(data, schema);
	} catch {
		return {
			logPayloadOmitted: true,
			reason: "redact_pii_failed"
		};
	}
}
/** Never throws; use when {@link scrubForExternalUse} runs on untrusted trees (e.g. Sentry `beforeSend`). */
function scrubForExternalUseSafe(value) {
	try {
		return scrubForExternalUse(value);
	} catch {
		return { ...LOG_SCRUB_OMITTED };
	}
}
/** Never throws; HTTP header objects may use odd prototypes or getters. */
function sanitizeHttpHeadersSafe(headers) {
	try {
		return sanitizeHttpHeaders(headers);
	} catch {
		return;
	}
}

export { LOG_SCRUB_OMITTED, UNKNOWN_PII_KEYS, getPiiFields, hashForSentryUserId, isUnknownPiiKey, redactPii, redactPiiFromObject, redactPiiSafe, redactUnknownPii, redactUnknownPiiSafe, sanitizeHttpHeaders, sanitizeHttpHeadersSafe, scrubForExternalUse, scrubForExternalUseSafe };
//# sourceMappingURL=piiUtils.mjs.map